import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconLoader2,
  IconPhoto,
  IconUpload,
  IconVideo,
  IconX,
} from '@tabler/icons-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * Caps mirror the strata-api service (`inbox-media.service.ts`). The server is
 * the authority — these exist so an operator learns about an oversize file
 * before spending a 25 MB upload on a 400.
 */
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const VIDEO_MAX_BYTES = 25 * 1024 * 1024;
const IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp'];
const VIDEO_MIMES = ['video/mp4', 'video/webm'];
const ACCEPT = [...IMAGE_MIMES, ...VIDEO_MIMES].join(',');

interface UploadResult {
  url: string;
  mediaType: 'image' | 'video';
  filename: string;
  sizeBytes: number;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Returns an operator-facing reason to refuse, or null when the file passes. */
function localRejection(file: File, imageOnly = false): string | null {
  if (IMAGE_MIMES.includes(file.type)) {
    return file.size > IMAGE_MAX_BYTES
      ? `Image is ${formatBytes(file.size)}. Maximum is 5 MB.`
      : null;
  }
  if (VIDEO_MIMES.includes(file.type)) {
    if (imageOnly)
      return 'Companion push media must be a JPG, PNG or WebP image.';
    return file.size > VIDEO_MAX_BYTES
      ? `Video is ${formatBytes(file.size)}. Maximum is 25 MB.`
      : null;
  }
  return `Unsupported file type${file.type ? ` "${file.type}"` : ''}. JPG, PNG or WebP images, or MP4/WebM video.`;
}

/** An aborted request is our own teardown, not something to show an operator. */
function isAbort(error: unknown): boolean {
  const code = (error as { code?: unknown })?.code;
  const name = (error as { name?: unknown })?.name;
  return (
    code === 'ERR_CANCELED' || name === 'CanceledError' || name === 'AbortError'
  );
}

function errorMessage(error: unknown): string {
  const response = (
    error as {
      response?: { status?: number; data?: { message?: unknown } };
    }
  )?.response;
  const message = response?.data?.message;
  const messages = Array.isArray(message) ? message : [message];
  if (
    response?.status === 503 &&
    messages.some(
      (item) =>
        typeof item === 'string' &&
        (item.includes('CAMPAIGN_MEDIA_BUCKET') ||
          item.includes('DEDICATED campaign-media bucket')),
    )
  ) {
    return 'Media uploads are not configured for this environment. You can still paste an existing public HTTPS image or video URL.';
  }
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && typeof message[0] === 'string')
    return message[0];
  return 'Upload failed. Please try again.';
}

/**
 * Drag-and-drop upload for the App Inbox hero image / promo video.
 *
 * Uploads land in a public GCS bucket and come back as a stable https URL.
 * Nova does not currently enforce a notification-retention period, so a
 * short-lived signed URL could expire while the message is still visible. The
 * URL is written into the variant's `imageUrl` or `videoUrl`; those inputs stay
 * editable so an operator with an existing CDN link can still paste one.
 */
export function InboxMediaUpload({
  imageUrl,
  videoUrl,
  onChange,
  disabled,
  imageOnly = false,
}: {
  imageUrl: string;
  videoUrl: string;
  onChange: (patch: { imageUrl?: string; videoUrl?: string }) => void;
  disabled?: boolean;
  imageOnly?: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * An upload that resolves after this component is gone would call the
   * captured `onChange` and write a media URL back into a variant the operator
   * has already cleared (clearComposer resets it) — the image reappears out of
   * nowhere. The controller aborts the request on unmount; `alive` is the
   * second belt, because an already-settled promise cannot be aborted.
   */
  const alive = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      const rejection = localRejection(file, imageOnly);
      if (rejection) {
        setError(rejection);
        toast.error(rejection);
        return;
      }
      setError(null);
      setUploading(true);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const form = new FormData();
        form.append('file', file);
        const response = await api.post<UploadResult>(
          '/campaigns/inbox-media/upload',
          form,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
          },
        );
        if (!alive.current) return;
        const result = response.data;
        onChange(
          result.mediaType === 'image'
            ? { imageUrl: result.url }
            : { videoUrl: result.url },
        );
        toast.success(
          `${result.mediaType === 'image' ? 'Image' : 'Video'} uploaded (${formatBytes(result.sizeBytes)})`,
        );
      } catch (uploadError) {
        if (isAbort(uploadError) || !alive.current) return;
        const message = errorMessage(uploadError);
        setError(message);
        toast.error(message);
      } finally {
        if (alive.current) setUploading(false);
      }
    },
    [imageOnly, onChange],
  );

  const busy = disabled || uploading;
  const hasMedia = Boolean(imageUrl.trim() || videoUrl.trim());

  return (
    <div className="space-y-2">
      {!hasMedia && (
        <div
          role="button"
          tabIndex={0}
          aria-label={
            imageOnly
              ? 'Upload companion push image'
              : 'Upload inbox image or video'
          }
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors',
            busy
              ? 'pointer-events-none opacity-60'
              : 'cursor-pointer hover:border-primary/50',
            error
              ? 'border-destructive bg-destructive/5'
              : dragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25',
          )}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            // Say which of the two ways the drop was wrong. A silent no-op on an
            // empty drop (dragging a URL, a folder on some browsers) reads as a
            // broken upload box.
            const files = event.dataTransfer.files;
            if (files.length > 1) {
              const message = 'One file at a time.';
              setError(message);
              toast.error(message);
              return;
            }
            const file = files[0];
            if (!file) {
              const message =
                'That drop had no file. JPG, PNG or WebP images, or MP4/WebM video.';
              setError(message);
              toast.error(message);
              return;
            }
            void handleFile(file);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={imageOnly ? IMAGE_MIMES.join(',') : ACCEPT}
            className="hidden"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
              event.target.value = '';
            }}
          />
          {uploading ? (
            <IconLoader2
              size={28}
              className="animate-spin text-muted-foreground"
            />
          ) : (
            <IconUpload
              size={28}
              className={cn(
                'text-muted-foreground',
                error && 'text-destructive',
              )}
            />
          )}
          <span className="text-sm text-muted-foreground">
            {uploading ? 'Uploading…' : 'Drag & drop or click to upload'}
          </span>
          <span className="text-xs text-muted-foreground/70">
            {imageOnly
              ? 'JPG, PNG or WebP up to 5 MB'
              : 'JPG, PNG or WebP up to 5 MB · MP4 or WebM up to 25 MB'}
          </span>
          {!imageOnly && (
            <span className="text-xs text-muted-foreground/70">
              Video is tap-to-play in the app; keep files small — data-first
              customers.
            </span>
          )}
          {error && (
            <span role="alert" className="text-xs font-medium text-destructive">
              {error}
            </span>
          )}
        </div>
      )}

      {hasMedia && (
        <div className="grid gap-3 sm:grid-cols-2">
          {imageUrl.trim() && (
            <MediaPreview
              label="Image"
              icon={<IconPhoto size={14} />}
              onClear={() => onChange({ imageUrl: '' })}
              disabled={busy}
            >
              <img
                src={imageUrl}
                alt="Inbox image preview"
                className="h-32 w-full rounded-md border border-border object-cover"
              />
            </MediaPreview>
          )}
          {videoUrl.trim() && (
            <MediaPreview
              label="Video"
              icon={<IconVideo size={14} />}
              onClear={() => onChange({ videoUrl: '' })}
              disabled={busy}
            >
              <video
                src={videoUrl}
                controls
                muted
                preload="metadata"
                className="h-32 w-full rounded-md border border-border object-cover"
              />
            </MediaPreview>
          )}
        </div>
      )}
    </div>
  );
}

function MediaPreview({
  label,
  icon,
  onClear,
  disabled,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  onClear: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          {icon}
          {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={disabled}
          aria-label={`Remove ${label.toLowerCase()}`}
        >
          <IconX size={14} />
        </Button>
      </div>
      {children}
    </div>
  );
}
